import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";
import { getCheckoutStatus } from "@/lib/api";
import { useCart } from "@/context/CartContext";

const MAX_ATTEMPTS = 8;

const CheckoutSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clearCart } = useCart();
  const [state, setState] = useState("checking"); // checking | success | error | timeout
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let attempts = 0;
    let active = true;

    const poll = async () => {
      if (!active) return;
      if (attempts >= MAX_ATTEMPTS) {
        setState("timeout");
        return;
      }
      attempts += 1;
      try {
        const data = await getCheckoutStatus(sessionId);
        if (data.payment_status === "paid") {
          if (!cleared.current) {
            cleared.current = true;
            clearCart();
          }
          setState("success");
          return;
        }
        if (data.status === "expired") {
          setState("error");
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        setTimeout(poll, 2000);
      }
    };
    poll();
    return () => { active = false; };
  }, [sessionId, clearCart]);

  const content = {
    checking: { icon: <Loader2 className="animate-spin text-primary" size={48} />, title: "Confirming your payment…", desc: "Hang tight, this only takes a moment." },
    success: { icon: <CheckCircle2 className="text-primary" size={48} />, title: "Payment successful!", desc: "Thank you for your order. A confirmation is on its way and your gear ships within 48 hours." },
    timeout: { icon: <Loader2 className="text-[#A1A1AA]" size={48} />, title: "Still processing", desc: "Your payment is taking longer than usual. Check your email for confirmation shortly." },
    error: { icon: <XCircle className="text-destructive" size={48} />, title: "Payment not completed", desc: "We couldn't confirm your payment. If you were charged, contact support@asicmod.com." },
  }[state];

  return (
    <div data-testid="checkout-success-page" className="mx-auto flex max-w-2xl flex-col items-center px-5 py-32 text-center">
      <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.4 }}>
        {content.icon}
      </motion.div>
      <h1 data-testid="checkout-status-title" className="mt-8 font-heading text-3xl font-semibold tracking-tight text-[#FAFAFA]">{content.title}</h1>
      <p className="mt-4 text-[#A1A1AA]">{content.desc}</p>
      <div className="mt-10 flex gap-4">
        <Link to="/shop" data-testid="success-continue" className="inline-flex items-center gap-2 rounded-sm bg-primary px-7 py-3.5 text-sm font-semibold text-[#09090B] transition-all hover:bg-[#EA580C]">
          Continue shopping <ArrowRight size={16} />
        </Link>
        <Link to="/" className="inline-flex items-center gap-2 rounded-sm border border-[#27272A] px-7 py-3.5 text-sm font-semibold text-[#FAFAFA] hover:border-primary">
          Home
        </Link>
      </div>
    </div>
  );
};

export default CheckoutSuccess;
